import type { ClientMessage, Role, ServerMessage } from '@hexkeep/shared';

type Listener = (msg: ServerMessage) => void;

/**
 * Auto-reconnecting WebSocket client. One per window. DM windows connect with role `dm`,
 * display windows with role `display` + displayId.
 */
export class HexkeepSocket {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private queue: ClientMessage[] = [];
  private retry = 0;
  private closed = false;
  status: 'connecting' | 'open' | 'closed' = 'connecting';
  private statusListeners = new Set<(s: HexkeepSocket['status']) => void>();

  constructor(
    private role: Role,
    private displayId?: string,
    private token?: string,
  ) {
    this.connect();
  }

  private url() {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${location.host}/ws`;
  }

  private setStatus(s: HexkeepSocket['status']) {
    this.status = s;
    for (const fn of this.statusListeners) fn(s);
  }

  private connect() {
    // close() may have been called while a reconnect was pending (e.g. the display unmounted).
    if (this.closed) return;
    this.setStatus('connecting');
    const ws = new WebSocket(this.url());
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      this.setStatus('open');
      ws.send(
        JSON.stringify({ type: 'hello', role: this.role, displayId: this.displayId, token: this.token }),
      );
      for (const m of this.queue.splice(0)) ws.send(JSON.stringify(m));
    };
    ws.onmessage = (e) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(e.data as string) as ServerMessage;
      } catch {
        return;
      }
      for (const fn of this.listeners) fn(msg);
    };
    ws.onclose = () => {
      this.setStatus('closed');
      if (this.closed) return;
      const delay = Math.min(5000, 250 * 2 ** this.retry++);
      setTimeout(() => this.connect(), delay);
    };
  }

  send(msg: ClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
    else if (msg.type !== 'camera.report' && msg.type !== 'pointer') this.queue.push(msg);
  }

  on(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Subscribe to one message type. */
  onType<T extends ServerMessage['type']>(type: T, fn: (msg: Extract<ServerMessage, { type: T }>) => void) {
    return this.on((m) => {
      if (m.type === type) fn(m as Extract<ServerMessage, { type: T }>);
    });
  }

  onStatus(fn: (s: HexkeepSocket['status']) => void) {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  close() {
    this.closed = true;
    this.ws?.close();
  }
}

let dmSocket: HexkeepSocket | null = null;
/** The DM window's shared socket (lazy). */
export function getDmSocket(): HexkeepSocket {
  if (!dmSocket) dmSocket = new HexkeepSocket('dm');
  return dmSocket;
}
